import React, { useEffect, useRef, useState } from 'react';
import { Dimensions, Modal, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import LhDatePicker from '../../../components/DatePicker/LhDatePicker';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme';

export interface DeliveryDateRange {
  start: string | null; // ISO date string yyyy-mm-dd
  end: string | null; // ISO date string yyyy-mm-dd
}

interface DeliveryDateRangeFilterProps {
  value: DeliveryDateRange;
  onChange: (value: DeliveryDateRange) => void;
  minDate?: string; // ISO yyyy-mm-dd
  maxDate?: string; // ISO yyyy-mm-dd
  disabled?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  testID?: string;
  mode?: 'range' | 'single';
  singleDatePlaceholder?: string;
}

const APPROX_PANEL_HEIGHT = 220;

// Formats an ISO (yyyy-mm-dd) date string as Analytics Hub-style date text.
const formatDisplayDate = (iso: string | null): string => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return '';
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const isoToDate = (iso: string | null): Date | null => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const dateToIso = (date: Date | null): string | null => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayIso = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDdMmYyyy = (iso: string | null): string => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return `${m}-${d}-${y}`;
};

/**
 * Delivery Date range filter — mirrors the KPIFilterDropdown trigger styling
 * (grey border by default, blue border/text when a value is active). The
 * Start/End Date panel renders inside a transparent Modal (same approach as
 * KPIFilterDropdown) positioned from the trigger's bounding rect, so it always
 * stacks above surrounding page content instead of being clipped/hidden by it.
 */
const DeliveryDateRangeFilter: React.FC<DeliveryDateRangeFilterProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  isOpen: isOpenProp,
  onOpenChange,
  testID,
  mode = 'range',
  singleDatePlaceholder = 'mm-dd-yyyy',
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === 'dark';
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isOpenProp !== undefined ? isOpenProp : internalOpen;
  const triggerRef = useRef<any>(null);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0, width: 240 });

  const setOpen = (open: boolean) => {
    if (onOpenChange) onOpenChange(open);
    else setInternalOpen(open);
  };

  const hasValue = !!(value.start || value.end);
  const triggerLabel = hasValue
    ? `${formatDisplayDate(value.start)}${value.start && value.end ? ' - ' : ''}${formatDisplayDate(value.end)}`
    : 'Select a Date';

  const recalculatePosition = (rect: { top: number; left: number; bottom: number; width: number }) => {
    const screenH = Dimensions.get('window').height;
    const spaceBelow = screenH - rect.bottom;
    const flipped = spaceBelow < APPROX_PANEL_HEIGHT + 8;
    setPanelPosition({
      top: flipped ? rect.top - APPROX_PANEL_HEIGHT : rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 240),
    });
  };

  const minIso = (a?: string, b?: string): string | undefined => {
    if (!a) return b;
    if (!b) return a;
    return a < b ? a : b;
  };

  const maxIso = (a?: string, b?: string): string | undefined => {
    if (!a) return b;
    if (!b) return a;
    return a > b ? a : b;
  };

  // Always disable future dates, even when external maxDate is not provided.
  const effectiveMaxDate = minIso(maxDate, getTodayIso());

  const startMin = minDate;
  const startMax = minIso(value.end ?? undefined, effectiveMaxDate);
  const endMin = maxIso(value.start ?? undefined, minDate);
  const endMax = effectiveMaxDate;

  const clampIsoDate = (iso: string | null, min?: string, max?: string): string | null => {
    if (!iso) return null;
    let next = iso;
    if (min && next < min) next = min;
    if (max && next > max) next = max;
    return next;
  };

  if (mode === 'single') {
    const selectedIso = value.end || value.start;
    const selectedDate = isoToDate(selectedIso);

    return (
      <View style={styles.container} testID={testID}>
        <LhDatePicker
          value={selectedDate}
          onChange={(date) => {
            const raw = dateToIso(date);
            const next = clampIsoDate(raw, minDate, effectiveMaxDate);
            onChange({ start: next, end: next });
          }}
          placeholder={singleDatePlaceholder}
          minimumDate={isoToDate(minDate ?? null) ?? undefined}
          maximumDate={isoToDate(effectiveMaxDate ?? null) ?? undefined}
          displayText={selectedIso ? formatDdMmYyyy(selectedIso) : undefined}
          colorScheme={isDarkMode ? 'dark' : 'light'}
          iconColor={isDarkMode ? colors.text.secondary : '#6A6D71'}
          disabledIconColor={isDarkMode ? colors.text.disabled : '#B0B7C3'}
          disabled={disabled}
        />
      </View>
    );
  }

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      if (Platform.OS === 'web' && triggerRef.current?.getBoundingClientRect) {
        const rect = triggerRef.current.getBoundingClientRect();
        recalculatePosition(rect);
      } else if (triggerRef.current?.measureInWindow) {
        triggerRef.current.measureInWindow((x: number, y: number, w: number, h: number) => {
          recalculatePosition({ top: y, left: x, bottom: y + h, width: w });
        });
      }
    }
    setOpen(!isOpen);
  };

  // Keep the panel positioned correctly on window resize/scroll while open (web only)
  useEffect(() => {
    if (Platform.OS !== 'web' || !isOpen) return;
    const handler = () => {
      const rect = triggerRef.current?.getBoundingClientRect?.();
      if (rect) recalculatePosition(rect);
    };
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <View style={styles.container} testID={testID}>
      <TouchableOpacity
        ref={triggerRef}
        style={[
          styles.trigger,
          isDarkMode && {
            backgroundColor: colors.input.background,
            borderColor: colors.input.border,
          },
          disabled && styles.triggerDisabled,
          isDarkMode && disabled && { backgroundColor: colors.background.elevated, borderColor: colors.border.default },
          !disabled && isOpen && styles.triggerActive,
        ]}
        onPress={handleToggle}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel="Delivery Date filter"
        testID={testID ? `${testID}-trigger` : undefined}
      >
        <Text
          style={[
            styles.triggerText,
            isDarkMode && { color: colors.text.inverse },
            !hasValue && styles.triggerPlaceholder,
            isDarkMode && !hasValue && { color: colors.text.secondary },
            disabled && styles.triggerTextDisabled,
            isDarkMode && disabled && { color: colors.text.disabled },
          ]}
          numberOfLines={1}
        >
          {triggerLabel}
        </Text>
        <MaterialIcons
          name="calendar-today"
          size={16}
          color={disabled ? (isDarkMode ? colors.text.disabled : '#B0B7C3') : (isDarkMode ? colors.text.secondary : '#7C8086')}
        />
      </TouchableOpacity>

      <Modal visible={isOpen} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={() => setOpen(false)}>
          <TouchableOpacity
            activeOpacity={1}
            onPress={(e) => e.stopPropagation()}
            style={[
              styles.panel,
              isDarkMode && {
                backgroundColor: colors.background.elevated,
                borderColor: colors.border.default,
              },
              {
                top: panelPosition.top,
                left: panelPosition.left,
                width: panelPosition.width,
              },
            ]}
          >
            <Text style={[styles.fieldLabel, isDarkMode && { color: colors.text.secondary }]}>Start Date</Text>
            <LhDatePicker
              value={isoToDate(value.start)}
              onChange={(date) => {
                const raw = dateToIso(date);
                onChange({ ...value, start: clampIsoDate(raw, startMin, startMax) });
              }}
              placeholder="Select a date"
              minimumDate={isoToDate(startMin ?? null) ?? undefined}
              maximumDate={isoToDate(startMax ?? null) ?? undefined}
              displayText={value.start ? formatDisplayDate(value.start) : undefined}
              colorScheme={isDarkMode ? 'dark' : 'light'}
              iconColor={isDarkMode ? colors.text.secondary : '#6A6D71'}
              disabledIconColor={isDarkMode ? colors.text.disabled : '#B0B7C3'}
            />

            <Text style={[styles.fieldLabel, styles.fieldLabelSpacing, isDarkMode && { color: colors.text.secondary }]}>End Date</Text>
            <LhDatePicker
              value={isoToDate(value.end)}
              onChange={(date) => {
                const raw = dateToIso(date);
                onChange({ ...value, end: clampIsoDate(raw, endMin, endMax) });
              }}
              placeholder="Select a date"
              minimumDate={isoToDate(endMin ?? null) ?? undefined}
              maximumDate={isoToDate(endMax ?? null) ?? undefined}
              displayText={value.end ? formatDisplayDate(value.end) : undefined}
              colorScheme={isDarkMode ? 'dark' : 'light'}
              iconColor={isDarkMode ? colors.text.secondary : '#6A6D71'}
              disabledIconColor={isDarkMode ? colors.text.disabled : '#B0B7C3'}
            />

            {hasValue && (
              <TouchableOpacity
                style={styles.clearBtn}
                onPress={() => onChange({ start: null, end: null })}
                accessibilityRole="button"
                accessibilityLabel="Clear delivery date"
              >
                <Text style={[styles.clearBtnText, isDarkMode && { color: colors.brand.primary }]}>Clear</Text>
              </TouchableOpacity>
            )}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const webInputStyle: any = {
  width: '100%',
  padding: '9px 10px',
  fontSize: 14,
  fontFamily: FontFamily.medium,
  color: '#2D2F33',
  border: '1px solid #DEE2E6',
  borderRadius: 8,
  outline: 'none',
  boxSizing: 'border-box',
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#DEE2E6',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    minHeight: 40,
  },
  triggerActive: {
    borderColor: '#0B5FC6',
  },
  triggerDisabled: {
    backgroundColor: '#F8F9FB',
    borderColor: '#E3E9F3',
  },
  triggerText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#2D2F33',
    marginRight: 8,
  },
  triggerPlaceholder: {
    color: '#7C8086',
  },
  triggerTextDisabled: {
    color: '#98A2B3',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.15)',
  },
  panel: {
    position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
    minWidth: 240,
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E9ECEF',
    padding: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  fieldLabel: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#6A6D71',
    marginBottom: 6,
  },
  fieldLabelSpacing: {
    marginTop: 12,
  },
  fieldFallback: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#2D2F33',
    paddingVertical: 9,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: '#DEE2E6',
    borderRadius: 8,
  },
  fieldValueHint: {
    marginTop: 6,
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#6A6D71',
  },
  clearBtn: {
    marginTop: 14,
    alignSelf: 'flex-start',
  },
  clearBtnText: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    color: '#0B5FC6',
  },
});

export default DeliveryDateRangeFilter;
